/* eslint-disable max-lines-per-function */
import React from 'react';
import PropTypes from 'prop-types';
import SimpleSnackbar from '../../Components/Snackbar/snackbar';
import CommonTextField from '../../Components/TextField/textField';
import SearchDropdown from '../../Components/Dropdown/dropdown';
import FormDialog from '../../Components/Modal/modal';
import './addeditcertification.scss';
import {
  ADD_EDIT_DIALOG,
  CANCEL_TEXT,
  SAVE,
  UPDATE,
  SELECT,
} from '../../Constants/strings';
import useCertificationForm from './useCertificationForm';

const AddCertificationForm = ({
  modalOpen,
  isEdit,
  handleClose,
  editProviderValue = '',
  editName = '',
  editProvider = '',
  editValidity = '',
  editCertificationId = '',
  initialCertificationName = '',
  editId,
  setRows,
  isEditMode,
  categoryList,
  editCategoryName,
  editSubCategoryName,
  lastUpdatedBy,
  editCategoryId,
  editSubcategoryId,
}) => {
  const {
    formData,
    errors,
    snackbar,
    handleInputChange,
    handleSubmit,
    resetValues,
  } = useCertificationForm({
    isEdit,
    editProviderValue,
    editName,
    editProvider,
    editValidity,
    editCertificationId,
    initialCertificationName,
    editId,
    setRows,
    handleClose, // Pass handleClose to the hook
    categoryList,
    editCategoryName,
    editSubCategoryName,
    lastUpdatedBy,
    editCategoryId,
    editSubcategoryId,
  });

  const handleCloseWithReset = () => {
    resetValues();
    handleClose();
  };

  return (
    <div>
      <FormDialog
        title={
          isEditMode
            ? ADD_EDIT_DIALOG.EDIT_CERTIFICATION
            : ADD_EDIT_DIALOG.ADD_CERTIFICATION
        }
        open={modalOpen}
        button1Action={handleCloseWithReset}
        dialogPaperCss="add-certification-dialog-paper"
        dialogCss="certificate-dialog"
        dialogBodycontent={
          <div className="certificate-dialog-body">
            <CommonTextField
              textCss="certificate-id"
              label={ADD_EDIT_DIALOG.PROVIDER_ID}
              value={formData.providerValue}
              onChange={(event) =>
                handleInputChange('providerValue', event.target.value)
              }
              placeholder={ADD_EDIT_DIALOG.PROVIDER_ID_PLACEHOLDER}
              autoFocus={false}
              size="small"
              id={isEdit ? 'edit-provider-name' : 'add-provider-name-text'}
            />
            {errors.providerIdError && (
              <div className="provider-id-error">
                <small>{errors.providerIdError}</small>
              </div>
            )}
            <CommonTextField
              textCss="certificate-id"
              label={ADD_EDIT_DIALOG.CERTIFICATION_NAME}
              value={formData.nameValue}
              onChange={(event) =>
                handleInputChange('nameValue', event.target.value)
              }
              placeholder={ADD_EDIT_DIALOG.CERTIFICATION_NAME_PLACEHOLDER}
              size="small"
              id={isEdit ? 'edit-certification-text' : 'add-certification-text'}
            />
            {errors.nameError && (
              <div className="provider-id-error">
                <small>{errors.nameError}</small>
              </div>
            )}
            <CommonTextField
              textCss="certificate-id"
              label={ADD_EDIT_DIALOG.CERTIFICATION_PROVIDER}
              value={formData.providerName}
              onChange={(event) =>
                handleInputChange('providerName', event.target.value)
              }
              placeholder={ADD_EDIT_DIALOG.CERTIFICATION_PROVIDER_NAME}
              size="small"
              id={isEdit ? 'edit-provider-text' : 'add-provider-text'}
            />
            {errors.providerNameError && (
              <div className="provider-id-error">
                <small>{errors.providerNameError}</small>
              </div>
            )}
            <CommonTextField
              textCss="certificate-id"
              label={ADD_EDIT_DIALOG.VALIDITY}
              value={formData.validity}
              onChange={(event) =>
                handleInputChange('validity', event.target.value)
              }
              placeholder={ADD_EDIT_DIALOG.VALIDITY_YEARS}
              size="small"
              id={isEdit ? 'edit-year-text' : 'add-year-text'}
            />

            {errors.validityYearError && (
              <div className="provider-id-error">
                <small>{errors.validityYearError}</small>
              </div>
            )}

            <div className="certification-dropdown">
              <SearchDropdown
                options={categoryList}
                label={ADD_EDIT_DIALOG.CATEGORY}
                placeholder={SELECT}
                value={formData?.categoryValue ?? ''}
                searchBox={true}
                onChange={(event) =>
                  handleInputChange('categoryValue', event.target.value)
                }
              />
              {errors.categoryError && (
                <div className="provider-id-error">
                  <small>{errors.categoryError}</small>
                </div>
              )}
            </div>

            <div className="certification-dropdown">
              <SearchDropdown
                options={formData.subCategoryList}
                label={ADD_EDIT_DIALOG.SUB_CATEGORY}
                placeholder={SELECT}
                value={formData?.subCategoryValue ?? ''}
                searchBox={true}
                onChange={(event) =>
                  handleInputChange('subCategoryValue', event.target.value)
                }
                disabled={!formData.categoryValue}
              />
              {errors.subCategoryError && (
                <div className="provider-id-error">
                  <small>{errors.subCategoryError}</small>
                </div>
              )}
            </div>
          </div>
        }
        button1={CANCEL_TEXT}
        button2={isEditMode ? UPDATE : SAVE}
        button2Action={handleSubmit}
        cancelButtonId={
          isEdit
            ? 'edit-certification-cancel-btn'
            : 'add-certification-cancel-btn'
        }
        submitButtonId={
          isEdit
            ? 'edit-certification-cancel-btn'
            : 'add-certification-submit-btn'
        }
      />
      <SimpleSnackbar
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        alertCss={`${isEdit ? 'edit-snackbar' : 'add-snackbar'} ${snackbar.severity
        }`}
      />
    </div>
  );
};

AddCertificationForm.propTypes = {
  modalOpen: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  isEdit: PropTypes.bool,
  editProviderValue: PropTypes.string,
  editName: PropTypes.string,
  editProvider: PropTypes.string,
  editValidity: PropTypes.number,
  editId: PropTypes.number,
  setRows: PropTypes.func.isRequired,
  editCertificationId: PropTypes.number,
  initialCertificationName: PropTypes.string,
  isEditMode: PropTypes.bool,
  categoryList: PropTypes.arrayOf(PropTypes.object).isRequired,
  editCategoryName: PropTypes.string,
  editSubCategoryName: PropTypes.string,
  lastUpdatedBy: PropTypes.string.isRequired,
  editCategoryId: PropTypes.number,
  editSubcategoryId: PropTypes.number,
};

export default AddCertificationForm;
